/**
 * The applications the console offers to connect (client notes 10/26, item 12): the menu behind "Connect New Source". It is a
 * list kept in the console, because the backend does not say which applications it can read; what is offered is whatever
 * of it is not connected yet. A source the tenant already has, in any state, is not offered again.
 */
import { sourceLogoSlug } from '../api-utils/sources/get_sources';

/** A security tool whose alerts are read, or a feed of threat intelligence (Threat Intel offers the second kind). */
export type CatalogKind = 'tool' | 'intel';

export interface CatalogApp {
  /** What the connect form takes as the provider ("checkpoint"). */
  provider: string;
  /** What the menu shows ("Check Point"). */
  name: string;
  kind: CatalogKind;
}

export const SOURCE_CATALOG: readonly CatalogApp[] = [
  { provider: 'aws', name: 'AWS CloudTrail', kind: 'tool' },
  { provider: 'okta', name: 'Okta', kind: 'tool' },
  { provider: 'crowdstrike', name: 'CrowdStrike Falcon', kind: 'tool' },
  { provider: 'paloalto', name: 'Palo Alto Networks', kind: 'tool' },
  { provider: 'splunk', name: 'Splunk', kind: 'tool' },
  { provider: 'fortinet', name: 'Fortinet', kind: 'tool' },
  { provider: 'zscaler', name: 'Zscaler', kind: 'tool' },
  { provider: 'azure', name: 'Microsoft Entra ID', kind: 'tool' },
  { provider: 'gcp', name: 'Google Cloud', kind: 'tool' },
  { provider: 'servicenow', name: 'ServiceNow', kind: 'tool' },
  { provider: 'jira', name: 'Jira', kind: 'tool' },
  { provider: 'checkpoint', name: 'Check Point', kind: 'tool' },
  { provider: 'sentinelone', name: 'SentinelOne', kind: 'tool' },
  { provider: 'carbonblack', name: 'Carbon Black', kind: 'tool' },
  { provider: 'cloudflare', name: 'Cloudflare', kind: 'tool' },
  { provider: 'github', name: 'GitHub', kind: 'tool' },
  { provider: 'virustotal', name: 'VirusTotal', kind: 'intel' },
  { provider: 'abuseipdb', name: 'AbuseIPDB', kind: 'intel' },
  { provider: 'misp', name: 'MISP', kind: 'intel' },
  { provider: 'recordedfuture', name: 'Recorded Future', kind: 'intel' },
  { provider: 'mandiant', name: 'Mandiant Advantage', kind: 'intel' },
];

/** The same key for the catalog and for a source, so "Microsoft Entra ID" and a source named "azure" are one application. */
function appKey(provider: string): string {
  return sourceLogoSlug({ provider, name: provider, candidate_source: provider });
}

/**
 * What of the catalog is left to connect: the applications of this kind that none of the tenant's sources already is.
 * `sources` is whatever the tenant has, connected or not (a source that is only inactive or in error still exists, and
 * is fixed from Connectors, not added a second time).
 */
export function availableApps(
  sources: readonly { provider?: string; name?: string; candidate_source?: string }[],
  kind: CatalogKind = 'tool'
): CatalogApp[] {
  const taken = new Set(sources.map((source) => sourceLogoSlug(source)).filter(Boolean));
  return SOURCE_CATALOG.filter((app) => app.kind === kind && !taken.has(appKey(app.provider)));
}
